import React, { useState, useRef } from 'react';
import { Download, UploadCloud, CheckCircle2, AlertCircle } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'https://heritage-os-api.uplaytv3117.workers.dev/api/v1';

export default function DataMigration() {
  const [isDragging, setIsDragging] = useState(false);
  const [importStatus, setImportStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [importMessage, setImportMessage] = useState('');
  const [exportStatus, setExportStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [exportMessage, setExportMessage] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleExport = async () => {
    try {
      setExportStatus('loading');
      setExportMessage('');
      const token = localStorage.getItem('jwt_token');
      const response = await fetch(`${API_URL}/migration/export`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'backup.json';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setExportStatus('idle');
    } catch (err) {
      setExportStatus('error');
      setExportMessage(err instanceof Error ? err.message : 'An error occurred during export');
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      await processFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      await processFile(e.target.files[0]);
    }
  };

  const processFile = async (file: File) => {
    if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
      setImportStatus('error');
      setImportMessage('Please upload a valid JSON file.');
      return;
    }

    try {
      setImportStatus('loading');
      setImportMessage('');
      const text = await file.text();
      const jsonData = JSON.parse(text); // validate JSON

      const token = localStorage.getItem('jwt_token');
      const response = await fetch(`${API_URL}/migration/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(jsonData)
      });

      if (!response.ok) {
        throw new Error('Import failed');
      }

      setImportStatus('success');
      setImportMessage('Data imported successfully!');
    } catch (err) {
      setImportStatus('error');
      setImportMessage(err instanceof Error ? err.message : 'An error occurred during import');
    }
  };

  return (
    <div className="p-6 h-full flex flex-col">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-slate-900 mb-2">Data Migration</h1>
        <p className="text-slate-600">Export your data or bulk import new records.</p>
      </div>

      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Export Section */}
        <div className="bg-white p-8 rounded-lg border border-slate-200 shadow-sm flex flex-col items-center justify-center text-center">
          <div className="h-16 w-16 bg-slate-100 rounded-full flex items-center justify-center mb-6">
            <Download className="h-8 w-8 text-slate-600" />
          </div>
          <h2 className="text-xl font-medium text-slate-900 mb-4">Export Data</h2>
          <p className="text-slate-600 mb-8 max-w-sm">
            Download a complete backup of your HeritageOS data including media, content, and settings in JSON format.
          </p>
          <button
            onClick={handleExport}
            disabled={exportStatus === 'loading'}
            className="flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-md font-medium hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download size={20} />
            {exportStatus === 'loading' ? 'Exporting...' : 'Download Backup'}
          </button>
          {exportStatus === 'error' && (
            <div className="mt-4 flex items-center gap-2 text-red-600 text-sm">
              <AlertCircle size={16} />
              <span>{exportMessage}</span>
            </div>
          )}
        </div>

        {/* Import Section */}
        <div className="bg-white p-8 rounded-lg border border-slate-200 shadow-sm flex flex-col items-center justify-center text-center">
          <div className="h-16 w-16 bg-slate-100 rounded-full flex items-center justify-center mb-6">
            <UploadCloud className="h-8 w-8 text-slate-600" />
          </div>
          <h2 className="text-xl font-medium text-slate-900 mb-4">Bulk Import</h2>
          <p className="text-slate-600 mb-8 max-w-sm">
            Upload a previously exported JSON backup file to restore or migrate data into HeritageOS.
          </p>

          <div
            className={`w-full max-w-md p-8 border-2 border-dashed rounded-lg transition-colors cursor-pointer ${
              isDragging
                ? 'border-slate-500 bg-slate-50'
                : 'border-slate-300 hover:border-slate-400'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".json,application/json"
              className="hidden"
            />
            <div className="flex flex-col items-center gap-3">
              <UploadCloud className="h-8 w-8 text-slate-400" />
              <div className="text-sm">
                <span className="font-medium text-slate-900">Click to upload</span> or drag and drop
              </div>
              <p className="text-xs text-slate-500">JSON files only</p>
            </div>
          </div>

          <div className="mt-6 min-h-[40px]">
            {importStatus === 'loading' && (
              <p className="text-slate-600 text-sm animate-pulse">Processing import...</p>
            )}
            {importStatus === 'success' && (
              <div className="flex items-center gap-2 text-green-600 text-sm">
                <CheckCircle2 size={16} />
                <span>{importMessage}</span>
              </div>
            )}
            {importStatus === 'error' && (
              <div className="flex items-center gap-2 text-red-600 text-sm">
                <AlertCircle size={16} />
                <span>{importMessage}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
